<script setup lang="ts">
import { computed } from 'vue'
import Progress from '@/components/shadcn/Progress.vue'

type StyleType =
  | 'default'   // 기본 스타일
  | 'primary'   // 강조 스타일 (파랑)
  | 'success'   // 성공 스타일 (초록)
  | 'warning'   // 경고 스타일 (주황)
  | 'danger'    // 위험 스타일 (빨강)

type SizeType = 'xs' | 'sm' | 'md' | 'lg'

type VariantType = 'linear' | 'circular'

const props = withDefaults(
  defineProps<{
    value?: number
    max?: number
    class?: string
    /** 스타일 프리셋 */
    styletype?: StyleType
    /** 크기 프리셋 */
    size?: SizeType
    /** 라벨 텍스트 */
    label?: string
    /** 진행률 퍼센트 표시 여부 */
    showLabel?: boolean
    /** 설명 텍스트 */
    description?: string
    /** 프로그레스 바 모양 */
    variant?: VariantType
    /** 무한 로딩 애니메이션 여부 */
    indeterminate?: boolean
  }>(),
  {
    value: 0,
    max: 100,
    styletype: 'default',
    size: 'sm',
    label: '',
    showLabel: true,
    description: '',
    variant: 'linear',
    indeterminate: false,
  },
)

/**
 * styletype -> class 매핑
 */
const STYLE_PRESETS: Record<StyleType, { class: string }> = {
  default: { class: '' },
  primary: { 
    class: '[&>div]:bg-blue-500',
  },
  success: { 
    class: '[&>div]:bg-green-500',
  },
  warning: { 
    class: '[&>div]:bg-amber-500',
  },
  danger: { 
    class: '[&>div]:bg-red-500',
  },
}

/**
 * size -> class 매핑
 */
const SIZE_PRESETS: Record<SizeType, { class: string }> = {
  xs: { 
    class: 'h-1',
  },
  sm: { 
    class: 'h-2',
  },
  md: { 
    class: 'h-3',
  },
  lg: { 
    class: 'h-4',
  },
}

/**
 * variant -> size 매핑 (circular용)
 */
const CIRCULAR_SIZE_PRESETS: Record<SizeType, { size: number }> = {
  xs: { 
    size: 48,
  },
  sm: { 
    size: 60,
  },
  md: { 
    size: 72,
  },
  lg: { 
    size: 88,
  },
}

/** 진행률 퍼센트 계산 */
const percentage = computed(() => {
  return Math.round((props.value! / props.max!) * 100)
})

/** 최종 바인딩: 직접 넘긴 class가 있으면 styletype과 size 기본값과 병합 */
const mapped = computed(() => {
  const stylePreset = STYLE_PRESETS[props.styletype!]
  const sizePreset = SIZE_PRESETS[props.size!]
  const finalClass = [stylePreset.class, sizePreset.class, props.class].filter(Boolean).join(' ')
  
  return {
    modelValue: props.value,
    max: props.max,
    class: finalClass,
  }
})

/** 원형 프로그레스 바 스타일 계산 */
const circularStyle = computed(() => {
  const sizePreset = CIRCULAR_SIZE_PRESETS[props.size!]
  
  // 색상 추출 - linear와 동일한 색상 사용
  let color = '#000000' // default: 검은색 (linear의 기본 색상과 동일)
  if (props.styletype === 'primary') color = '#3b82f6' // blue-500
  else if (props.styletype === 'success') color = '#10b981' // green-500
  else if (props.styletype === 'warning') color = '#f59e0b' // amber-500
  else if (props.styletype === 'danger') color = '#ef4444' // red-500
  
  return {
    size: sizePreset.size,
    color,
  }
})

/** indeterminate 상태에서 표시할 텍스트 */
const indeterminateText = computed(() => {
  return props.indeterminate ? '로딩 중...' : `${percentage.value}%`
})

/** Linear indeterminate 색상 계산 */
const linearIndeterminateColor = computed(() => {
  let color = '#000000' // default: 검은색
  if (props.styletype === 'primary') color = '#3b82f6' // blue-500
  else if (props.styletype === 'success') color = '#10b981' // green-500
  else if (props.styletype === 'warning') color = '#f59e0b' // amber-500
  else if (props.styletype === 'danger') color = '#ef4444' // red-500
  
  return color
})
</script>

<template>
  <div class="space-y-2">
    <!-- Linear Progress용 상단 라벨 -->
    <div v-if="props.variant === 'linear' && (props.label || props.showLabel || props.indeterminate)" class="flex justify-between items-center">
      <p v-if="props.label" class="text-sm font-medium text-gray-700">
        {{ props.label }}
      </p>
      <p v-if="props.showLabel || props.indeterminate" class="text-sm font-medium text-gray-600">
        {{ indeterminateText }}
      </p>
    </div>
    
    <!-- Circular Progress용 상단 라벨 -->
    <div v-if="props.variant === 'circular' && props.label" class="text-center mb-2">
      <p class="text-sm font-medium text-gray-700">{{ props.label }}</p>
    </div>
    
    <!-- Linear Progress -->
    <div v-if="props.variant === 'linear'">
      <!-- Indeterminate Linear -->
      <div v-if="props.indeterminate" class="relative overflow-hidden rounded-full bg-gray-200" :class="SIZE_PRESETS[props.size!].class">
        <div 
          class="absolute h-full w-full animate-pulse"
          :style="{ backgroundColor: linearIndeterminateColor }"
        />
        <div 
          class="absolute h-full w-1/3 animate-[indeterminate-linear_2s_infinite]"
          :style="{ backgroundColor: linearIndeterminateColor }"
        />
      </div>
      <!-- Determinate Linear -->
      <Progress v-else v-bind="mapped" />
    </div>
    
    <!-- Circular Progress -->
    <div v-else-if="props.variant === 'circular'" class="flex justify-center">
      <div class="relative" :style="{ width: circularStyle.size + 'px', height: circularStyle.size + 'px' }">
        <!-- Indeterminate Circular -->
        <div v-if="props.indeterminate">
          <svg class="w-full h-full animate-spin" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="45"
              stroke="#e5e7eb"
              stroke-width="8"
              fill="none"
            />
            <circle
              cx="50"
              cy="50"
              r="45"
              :stroke="circularStyle.color"
              stroke-width="8"
              fill="none"
              stroke-linecap="round"
              stroke-dasharray="70 213"
              stroke-dashoffset="0"
              class="animate-[indeterminate-circular_2s_ease-in-out_infinite]"
            />
          </svg>
        </div>
        <!-- Determinate Circular -->
        <div v-else>
          <svg class="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            <!-- Background circle -->
            <circle
              cx="50"
              cy="50"
              r="45"
              stroke="#e5e7eb"
              stroke-width="8"
              fill="none"
            />
            <!-- Progress circle -->
            <circle
              cx="50"
              cy="50"
              r="45"
              :stroke="circularStyle.color"
              stroke-width="8"
              fill="none"
              stroke-linecap="round"
              :stroke-dasharray="283"
              :stroke-dashoffset="283 - (283 * percentage / 100)"
              class="transition-all duration-300 ease-in-out"
            />
          </svg>
        </div>
        <!-- Center text -->
        <div v-if="props.showLabel || props.indeterminate" class="absolute inset-0 flex items-center justify-center">
          <span class="text-sm font-medium text-gray-700">{{ indeterminateText }}</span>
        </div>
      </div>
    </div>
    
    <!-- Circular Progress용 하단 description -->
    <div v-if="props.variant === 'circular' && props.description" class="text-center mt-2">
      <p class="text-xs text-gray-500">{{ props.description }}</p>
    </div>
    
    <!-- Linear Progress용 하단 description -->
    <p v-if="props.variant === 'linear' && props.description" class="text-xs text-gray-500">
      {{ props.description }}
    </p>
  </div>
</template>

<style>
@keyframes indeterminate-linear {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(400%);
  }
}

@keyframes indeterminate-circular {
  0% {
    stroke-dasharray: 70 213;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 140 143;
    stroke-dashoffset: -70;
  }
  100% {
    stroke-dasharray: 70 213;
    stroke-dashoffset: -213;
  }
}
</style>
